import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import ConsumerForm from "../components/ConsumerForm";
import CategoryCard from "../components/CategoryCard";
import SiteActivity from "../components/SiteActivity";
import { MapPin, Layers, Award } from "lucide-react";
import { trackEvent } from "../lib/tracker";

export default function HomePage() {
    const [categories, setCategories] = useState([]);

    useSEO({
        title: "Free Legal Information & Local Attorney Matching",
        description:
            "Ask a legal question in plain English, spot the issues, and get clear next steps. Free legal information across all 50 U.S. states — and connect with a local attorney by county and practice area.",
        path: "/",
    });

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCategories(data));
    }, []);

    const focusForm = (slug) => {
        document.getElementById("consumer-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (!slug) return;
        setTimeout(() => {
            const sel = document.querySelector('[data-testid="consumer-category-select"]');
            if (sel) {
                const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value").set;
                setter.call(sel, slug);
                sel.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }, 350);
    };

    return (
        <>
            {/* HERO + CONSUMER INTAKE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 lg:pt-20 lg:pb-12">
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    <div className="lg:col-span-6 animate-fade-up">
                        <span className="ll-pill mb-6" data-testid="hero-eyebrow">Free Legal Information</span>
                        <h1 className="font-serif font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="hero-headline">
                            Ask a legal question.<br />
                            Get clear next steps.<br />
                            <span className="text-primary">Understand your rights.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            Free, plain-language legal information for all 50 states and the District of Columbia.
                            Tell us what's going on, choose a category, and we'll help you spot the issues and figure out your next move.
                        </p>
                    </div>

                    {/* Consumer form */}
                    <div className="lg:col-span-6 animate-fade-up" id="consumer-form">
                        <ConsumerForm />
                    </div>
                </div>
                {/* Site Activity — renders only when above the 100/mo threshold. */}
                <SiteActivity className="mt-10 lg:mt-14" />
            </section>

            {/* PRACTICE CHANNELS */}
            <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="grid lg:grid-cols-12 gap-10 mb-10">
                    <div className="lg:col-span-4">
                        <div className="ll-label mb-3">Practice Areas</div>
                        <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-3">Choose what's going on.</h2>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                            Each category has its own common questions and guidance.
                            Tap one to start with a relevant prompt — or just type your own question above.
                        </p>
                    </div>
                    <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {categories.map((c) => (
                            <CategoryCard key={c.slug} category={c} onSelect={(cat) => focusForm(cat.slug)} />
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border">
                <div className="ll-label mb-3">How LocalLawFinder Works</div>
                <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-10">Three steps. No legal jargon.</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="ll-card">
                        <Layers className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">1. Pick a category</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Criminal Defense, Personal Injury, Family Law, Housing, Employment, Civil Rights, Post-Conviction Support, or Entertainment Law.</p>
                    </div>
                    <div className="ll-card">
                        <MapPin className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">2. Enter your ZIP</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">We map you to a county so the guidance — and any optional attorney match — fits your local area.</p>
                    </div>
                    <div className="ll-card">
                        <Award className="h-7 w-7 text-primary mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">3. Get clear next steps</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">Issue spotting and concrete next steps. If you want one, you can also find a local attorney — but it's optional.</p>
                    </div>
                </div>

                <div className="mt-12 ll-card bg-accent/40 border-primary/20">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h3 className="font-serif text-lg font-semibold">Legal information only — not legal advice.</h3>
                            <p className="text-sm text-muted-foreground mt-1">Especially for criminal matters, please speak with a licensed attorney for guidance on your situation.</p>
                        </div>
                        <button
                            onClick={() => { trackEvent("button_click", "Ask your question (bottom CTA)"); focusForm(); }}
                            className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 whitespace-nowrap"
                            data-testid="bottom-cta-ask">
                            Ask your question
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}
